"use client";

import { useCallback, useId, useMemo, type ReactElement } from "react";
import { cn } from "@/lib/utils/cn";
import { Radio } from "./Radio";
import { RadioGroupContext } from "./RadioGroupContext";
import type { RadioGroupProps } from "./types";

function RadioGroupImpl<T extends string = string>({
  value,
  onChange,
  children,
  name,
  disabled,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: RadioGroupProps<T>): ReactElement {
  const generatedName = useId();
  const resolvedName = name ?? generatedName;

  const handleChange = useCallback(
    (nextValue: string) => onChange(nextValue as T),
    [onChange],
  );

  const contextValue = useMemo(
    () => ({
      name: resolvedName,
      value,
      onChange: handleChange,
      disabled,
    }),
    [resolvedName, value, handleChange, disabled],
  );

  return (
    <RadioGroupContext.Provider value={contextValue}>
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={cn("flex flex-col gap-2", className)}
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

type RadioGroupComponent = (<T extends string = string>(
  props: RadioGroupProps<T>,
) => ReactElement) & {
  Radio: typeof Radio;
};

RadioGroupImpl.Radio = Radio;

export const RadioGroup = RadioGroupImpl as RadioGroupComponent;
